import {
  DataProjectionCoordinate,
  getEulerInverse,
  SceneCoordinate,
  SphericalPosition
} from '../projections';
import { Viewer } from '../main';
import {
  Asset,
  AssetType,
  CameraData
} from '../../../redux/assets-slice';
import {
  AmbientLight,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Euler,
  MathUtils,
  Matrix4,
  Mesh,
  PerspectiveCamera,
  Scene,
  Texture,
  Vector3
} from 'three';
import { MarkerMaterial, PanoramicImageMaterial, PlanarImageMaterial } from "../rendering";
import { CustomMouseEvent, MouseControls } from "../controls";
import { toast } from "../../../app";
import { SceneCameraState } from "../../../redux/camera-slice";
import { t } from "../../../localization";
import { TextureCache } from "./texture-cache";
import {
  AerialCameras,
  PanoramicCameraFile,
  PlanarCameraFile,
  PlanarImage,
  SceneViewCameras
} from '.';
import VectorSource from 'ol/source/Vector';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import {Feature} from "ol";
import { MarkerNavType } from '../../../redux/settings-slice';
import {isEqual} from 'lodash';

const thumbnailCache = new TextureCache(40, true);
const standardCache = new TextureCache(10, false);

export interface ImagePixel {
  u: number,
  v: number
}

export interface CameraLoadOptions {
  fov?: number
  angles?: number[]
  lookat?: DataProjectionCoordinate
  centerOnImage?: boolean
}

export type CameraArguments = ConstructorParameters<typeof CameraImage>

export type CameraFileArguments = ConstructorParameters<typeof CameraFile>

class ImagePosition {
  private _scene: SceneCoordinate;
  private _aerial: number[];
  private _value: Vector3;

  private input: Vector3;
  private image: CameraImage;

  constructor(image: CameraImage, position) {
    this.image = image;
    this.input = new Vector3(...position);
  }

  get offset() {
    let matrix = new Matrix4()
      .copy(this.image.matrix)
      .transpose();

    return new Vector3()
      .setFromMatrixPosition(matrix);
  }

  get value() {
    if (!this._value) {
      const euler = this.image.rotation.input;

      const offset = new Vector3()
        .copy(this.offset)
        .applyEuler(euler);

      const position = new Vector3()
        .copy(this.input)
        .add(offset);

      this._value = position;
    }

    return this._value;
  }

  get scene() {
    if (!this._scene) {
      const value = new DataProjectionCoordinate(this.value).toScene();
      this._scene = value;
    }

    return this._scene;
  }

  get aerial() {
    if (!this._aerial) {
      const value = new DataProjectionCoordinate(this.value).toAerial();
      this._aerial = value;
    }

    return this._aerial;
  }

  reset() {
    this._value = null;
    this._scene = null;
    this._aerial = null;
  }
}

class ImageRotation {
  private _euler: Euler;

  public array = [0,0,0];
  public input: THREE.Euler;
  private image: CameraImage;

  constructor(image: CameraImage, rotation) {
    this.image = image;
    this.setRotation(rotation);
  }

  get matrix() {
    return new Matrix4()
      .copy(this.image.matrix)
      .setPosition(0,0,0);
  }

  get adjustment() {
    if (this.image.adjustment) {
      const angles = this.image.adjustment;
      const x = -1 * MathUtils.degToRad(angles.x);
      const y = -1 * MathUtils.degToRad(angles.y);
      const z = -1 * MathUtils.degToRad(angles.z);
      return new Euler(x, y, z);
    }

    return new Euler(0,0,0);
  }

  get euler() {
    if (!this._euler) {
      const rotationWithTransform = multiplyRotations(
        this.input, this.matrix);

      const rotationWithAdjustment = multiplyRotations(
        rotationWithTransform, this.adjustment);

      const value = new Euler().setFromRotationMatrix(
        rotationWithAdjustment);

      this._euler = value;
    }

    return this._euler;
  }

  get scene() {
    return this.euler;
  }

  get degrees() {
    const x = -1 * MathUtils.radToDeg(this.euler.x);
    const y = -1 * MathUtils.radToDeg(this.euler.y);
    const z = -1 * MathUtils.radToDeg(this.euler.z);
    return new Euler(x,y,z);
  }

  get inputInverted() {
    return getEulerInverse(this.input);
  }

  setRotation(rotation) {
    this.array = rotation;
    const x = -1 * MathUtils.degToRad(rotation[0]);
    const y = -1 * MathUtils.degToRad(rotation[1]);
    const z = -1 * MathUtils.degToRad(rotation[2]);
    this.input = new Euler(x,y,z);
  }

  reset() {
    this._euler = null;
  }

  clear() {
    this.setRotation([0,0,0]);
  }
}

export class CameraFeature extends Feature<any> {
  public camera: CameraImage;

  constructor(opts) {
    super(opts);
    this.camera = opts.camera;
  }
}

export class CameraImage {
  public id: string;
  public name: string;
  public path: string;
  public adjustment: Vector3
  public object: Mesh;
  public feature: CameraFeature;
  public enabled = false;
  public loaded = false;
  public loading = false;
  public missing = false;
  public geometry: BoxGeometry;
  public cameraData: CameraData;
  public cameraFile: CameraFile;
  private controller: AbortController;
  public imageWidth = 0;
  public imageHeight = 0;
  public scene: Scene;
  public camera: PerspectiveCamera;
  public matrix: Matrix4 = new Matrix4();
  public controls: MouseControls;
  public position: ImagePosition;
  public rotation: ImageRotation;

  protected material: PanoramicImageMaterial | PlanarImageMaterial;
  private texture: CanvasTexture;
  protected missingTexture: Texture;

  constructor(cameraFile: CameraFile, data: CameraData, scene: Scene, camera: PerspectiveCamera, controls: MouseControls) {
    this.cameraData = data;
    this.cameraFile = cameraFile;

    this.id = data.id;
    this.name = data.name;
    this.path = data.path;

    this.camera = camera;
    this.scene = scene;
    this.controls = controls;

    const position = [data.x, data.y, data.z];
    this.position = new ImagePosition(this, position);

    const rotation = [data.roll, data.pitch, data.yaw];
    this.rotation = new ImageRotation(this, rotation);

    this.reset();
  }

  setMatrix(matrix: Matrix4) {
    this.matrix = matrix;
    this.rotation.reset();
    this.position.reset();
  }

  setAdjustment(adjustments) {
    this.adjustment = (this.id in adjustments)
      ? new Vector3(...adjustments[this.id])
      : null;

    this.rotation.reset();
  }

  pixelToDirection(pixel: ImagePixel) : Vector3 {
    console.warn("Not implemented");
    return new Vector3();
  }

  directionToPixel(direction: Vector3) : ImagePixel {
    console.warn("Not implemented");
    return null;
  }

  getFeature(): Feature {
    console.warn("Not implemented");
    return null;
  }

  /** @private */
  setCameraPosition() {
    this.camera.position.copy(this.position.scene);
    this.object.position.copy(this.position.scene);
    this.object.rotation.copy(this.rotation.scene);
  }

  /** @private */
  setCameraParameters(opts: CameraLoadOptions) {
    if (opts.fov) {
      this.controls.fov = opts.fov;
    }

    if (opts.lookat) {
      let position = new DataProjectionCoordinate(opts.lookat).toScene();
      this.controls.angles = positionToCameraAngle(
        this.object.position, position);
    } else if (opts.angles) {
      this.controls.angles = opts.angles;
    }
  }

  /** @private */
  async grabImageFromPath(thumbnail: boolean) {
    this.controller = new AbortController();

    const textureCache = thumbnail
      ? thumbnailCache
      : standardCache;

    const response = await textureCache.get(this.path, this.controller);

    this.texture = response.texture;
    this.missing = response.missing;

    if (this.texture && !thumbnail) {
      this.imageWidth = response.width;
      this.imageHeight = response.height;
    }

    if (!this.enabled) return;

    this.updateMaterial();

    if (this.missing) {
      toast.error(t("toast.image-load-error", {
        name: this.name
      }));
    }
  }

  setObjectVisible(state) {
    this.object.visible = state;
  }

  activate(opts: CameraLoadOptions = {}) {
    this.setCameraPosition();
    this.setCameraParameters(opts);
    this.setObjectVisible(true);

    this.loaded = true;
    this.loading = false;
  }

  initialize() {
    this.enabled = true;
    this.loaded = false;
    this.loading = true;
    this.generateObject();
  }

  async load() {
    const loadThumbnail = this.cameraFile.cloud;

    if (!loadThumbnail) {
      // Load full image and return
      await this.loadFullImage();
      return;
    }

    // Load preview image
    await this.loadThumbnail();
    if (this.missing) return;

    // Load full image, but dont await results
    this.loadFullImage();
  }

  /** @private */
  async loadThumbnail() {
    await this.grabImageFromPath(true);
  }

  /** @private */
  async loadFullImage() {
    await this.grabImageFromPath(false);
  }

  applyTempRotation(angles) {
    const angleEuler = new Euler(
      -1 * MathUtils.degToRad(angles[0]),
      -1 * MathUtils.degToRad(angles[1]),
      -1 * MathUtils.degToRad(angles[2])
    );

    const tempRotation = multiplyRotations(
      this.rotation.input, angleEuler);
    const euler = new Euler()
      .setFromRotationMatrix(tempRotation);

    if (this.object) {
      this.object.rotation.copy(euler);
    }

  }

  /** @private */
  generateObject() {
    const geometry = new BoxGeometry(10, 10, 10);
    const mesh = new Mesh(geometry, this.material);
    mesh.visible = false;

    this.object = mesh;
    this.scene.add(this.object);
  }

  /** @private */
  updateMaterial() {
    this.material.tImage = this.missing
      ? this.missingTexture
      : this.texture;
  }

  destroy() {
    if (!this.enabled) return false;

    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }

    if (this.object) {
      this.scene.remove(this.object);
    }

    if (this.material) {
      this.material.dispose();
    }

    if (this.geometry) {
      this.geometry.dispose();
    }

    if (this.texture) {
      this.texture.dispose();
    }

    this.reset();
  }

  reset() {
    this.object = null;
    this.enabled = false;
    this.geometry = null;
    this.texture = null;

    if (this.material) {
      this.material.tImage = null;
    }
  }
}

export class CameraFile {
  public id: string;
  public folderID: string;
  public cloud: boolean;
  public visible = false;
  private updated = false;

  public cameras: CameraImage[] = [];
  protected cameraHandler: CameraHandler;

  private standardMaterial: MarkerMaterial
  private standardMesh: Mesh
  private pickingMaterial: MarkerMaterial
  private pickingMesh: Mesh
  private geometry: BufferGeometry;

  private mapSource: VectorSource<any>;
  private mapLayerForeground: WebGLPointsLayer<any>;
  public mapLayerBackground: WebGLPointsLayer<any>;

  private minCameraRadius = 1.5;
  private maxCameraRadius = 6;

  private numVertices = 6;
  private uvs: number[] = [0, 1, 0, 0, 1, 1, 0, 0, 1, 0, 1, 1];
  private corners: number[] = [0, 2, 1, 2, 3, 1];
  private vertices: Vector3[] = [
    new Vector3(-0.5, 0.5, 0),
    new Vector3(0.5, 0.5, 0),
    new Vector3(-0.5, -0.5, 0),
    new Vector3(0.5, -0.5, 0)
  ]

  constructor(cameraHandler: CameraHandler, asset: Asset) {
    this.cameraHandler = cameraHandler;
    this.id = asset.id;
    this.cloud = asset.cloud;
    this.folderID = asset.folderID;

    // Add new camera data
    const cameraData = asset.data as CameraData[];
    cameraData.forEach(data => {
      const camera = this.getNewCamera(data);
      camera.setMatrix(cameraHandler.matrix);
      camera.setAdjustment(cameraHandler.adjustments);
      this.cameras.push(camera);
    });

    // NOTE: Special hack for mosaic
    if (this.id === "9xlU4x4onuF02ixxFvyXk") {
      this.cameras.sort((a,b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    }

    this.initSceneView();
    this.initAerialView();
  }

  get viewer() {
    return this.cameraHandler.viewer;
  }

  get scene() {
    return this.cameraHandler.markerScene;
  }

  get picker() {
    return this.viewer.gpuPickers.markers;
  }

  get map() {
    return this.viewer.minimap.map;
  }

  get meshes() {
    return [this.standardMesh, this.pickingMesh];
  }

  get materials() {
    return [this.standardMaterial, this.pickingMaterial];
  }

  getByID(cameraID: string): CameraImage {
    return this.cameras.find(camera => camera.id === cameraID);
  }

  getByName(name: string): CameraImage {
    return this.cameras.find(camera => camera.name === name);
  }

  getByPickerID(pickerID: number): CameraImage {
    const attributes = this.pickingMesh.geometry.attributes;
    const gpuIndex = attributes.gpu_index.array as number[];
    const arrIndex = gpuIndex.indexOf(pickerID);
    if (arrIndex === -1) return null;

    const cameraIndex = attributes.camera_index.array[arrIndex];
    return this.cameras[cameraIndex];
  }

  initSceneView() {
    const numCameras = this.cameras.length;
    const arrayLength = numCameras * this.numVertices;

    const selected = new Float32Array(arrayLength);
    const visible = new Float32Array(arrayLength);
    const markerShape = new Float32Array(arrayLength);
    const positions = new Float32Array(arrayLength * 3);
    const uvs = new Float32Array(arrayLength * 2);
    const indices = new Int32Array(arrayLength);
    const cameraIndices = new Int32Array(arrayLength);

    this.cameras.forEach((camera, cameraIndex) => {
      // Set the marker shape. Default is circular
      let cameraShape = camera instanceof PlanarImage ? 1.0 : 0.0;

      // Assign our uv values
      const numUvs = this.uvs.length;
      this.uvs.forEach((uv, uvIndex) => {
        let index = (numUvs * cameraIndex) + uvIndex;
        uvs[index] = uv;
      });

      // Create attribute arrays for each vertex in each cam.
      const numVertices = this.numVertices;
      for (var i = 0; i < this.numVertices; i++) {
        let index = (numVertices * cameraIndex) + i;
        selected[index] = 0.0;
        visible[index] = 1.0;
        indices[index] = 0.0;
        markerShape[index] = cameraShape;
        cameraIndices[index] = cameraIndex;
      }
    });

    const geometry = new BufferGeometry();
    geometry.setAttribute('selected', new BufferAttribute(selected, 1));
    geometry.setAttribute('visible', new BufferAttribute(visible, 1));
    geometry.setAttribute('marker_shape', new BufferAttribute(markerShape, 1));
    geometry.setAttribute('gpu_index', new BufferAttribute(indices, 1));
    geometry.setAttribute('camera_index', new BufferAttribute(cameraIndices, 1));
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new BufferAttribute(uvs, 2));

    this.standardMaterial = new MarkerMaterial(false);
    this.standardMesh = new Mesh(geometry, this.standardMaterial);
    this.standardMesh.visible = false;
    this.scene.add(this.standardMesh);

    this.pickingMaterial = new MarkerMaterial(true);
    this.pickingMesh = new Mesh(geometry, this.pickingMaterial);
    this.pickingMesh.visible = false;
    this.picker.add(this.pickingMesh);

    this.geometry = geometry;
  }

  initAerialView() {
    this.mapSource = new VectorSource({
      wrapX: false
    });

    this.mapLayerBackground = new WebGLPointsLayer({
      zIndex: 1,
      source: this.mapSource,
      style: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, this.minCameraRadius,
          18, this.maxCameraRadius
        ],
        "circle-fill-color": "#000000"
      },
    });

    this.mapLayerForeground = new WebGLPointsLayer({
      zIndex: 1,
      source: this.mapSource,
      style: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          10, this.minCameraRadius - 1,
          18, this.maxCameraRadius - 1
        ],
        "circle-fill-color": "#FFFFFF"
      },
    });

    this.map.addLayer(this.mapLayerBackground);
    this.map.addLayer(this.mapLayerForeground);
  }

  updateMatrix(matrix: Matrix4) {
    this.cameras.forEach(camera => {
      camera.setMatrix(matrix);
    });

    this.updated = false;
    this.updateViews();
  }

  updateAdjustments(adjustments = {}) {
    this.cameras.forEach(camera => {
      camera.setAdjustment(adjustments);
    });
  }

  updateSceneView() {
    const positions = this.geometry.attributes.position;

    this.cameras.forEach((camera, cameraIndex) => {
      const position = camera.position.scene;
      const numCorners = this.corners.length;
      this.corners.forEach((corner, cornerIndex) => {
        let index = (numCorners * cameraIndex) + cornerIndex;
        let vertex = this.vertices[corner];

        let x = vertex.x + position.x;
        let y = vertex.y + position.y;
        let z = vertex.z + position.z;

        positions.setXYZ(index, x, y, z);
      });
    });

    this.updateUniforms();
    this.updatePicker();
  }

  updateAerialView() {
    const features = this.cameras
      .map(camera => camera.getFeature())
      .filter(feature => feature);

    this.mapSource.clear();
    this.mapSource.addFeatures(features);
  }

  getNewCamera(data: CameraData): CameraImage {
    console.warn("Not implemented");
    return null;
  }

  setSelected(camera: CameraImage) {
    this.meshes.forEach(mesh => {
      const attributes = mesh.geometry.attributes;
      const selected = attributes.selected.array as number[];
      selected.fill(0.0);

      if (this.cameras.includes(camera)) {
        const index = this.cameras.indexOf(camera);
        const baseIndex = this.numVertices * index;
        for (let i = 0; i < this.numVertices; i++) {
          selected[baseIndex + i] = 1.00;
        }
      }

      attributes.selected.needsUpdate = true;
    });
  }

  updateVisibility(state: boolean) {
    this.visible = state;

    if (this.mapLayerBackground) {
      this.mapLayerForeground.setVisible(state);
      this.mapLayerBackground.setVisible(state);
    }

    if (this.standardMesh) {
      this.standardMesh.visible = state;
    }

    if (this.pickingMesh) {
      this.pickingMesh.visible = state;
    }

    if (!this.visible) {
      this.cameras.forEach(camera => {
        camera.destroy();
      });
    }

    this.updateViews();
    this.updatePicker();
  }

  /** @private */
  updateViews() {
    if (this.updated || !this.visible) return;

    this.updateSceneView();
    this.updateAerialView();
    this.updated = true;
  }

  updatePicker() {
    this.picker.needsUpdate = true;
  }

  updateUniforms() {
    const camera = this.viewer.camera;
    const position = camera.position;
    const rotation = camera.rotation.toVector3();
    const cameraLoading = this.viewer.initialCameraLoading;

    const current = this.cameraHandler.current;
    const currentIndex = this.cameras.indexOf(current);

    const visible = this.visible
      && this.cameraHandler.markersVisible
      && !cameraLoading;

    // Set mesh visibility
    this.meshes.forEach(mesh => {
      mesh.visible = visible;
    });

    // Update shader parameters
    this.materials.forEach((material: MarkerMaterial) => {
      material.currentCameraRotation = rotation;
      material.currentCameraPosition = position;
      material.windowHeight = window.innerHeight;
      material.currentIndex = currentIndex;
      material.orbitMode = this.viewer.orbitState;
      material.cameraHeight = this.cameraHandler.cameraHeight;
      material.fov = MathUtils.degToRad(camera.fov);
      material.needsUpdate = true;
    });
  }

  update() {
    this.updateUniforms();
  }

  destroy() {
    if (this.mapLayerBackground) {
      this.map.removeLayer(this.mapLayerForeground);
      this.map.removeLayer(this.mapLayerBackground);
    }

    if (this.standardMesh) {
      this.scene.remove(this.standardMesh);
      this.standardMaterial.dispose();
    }

    if (this.pickingMesh) {
      this.picker.remove(this.pickingMesh);
      this.pickingMaterial.dispose();
    }

    this.cameras.forEach(camera => {
      camera.destroy();
    });

    this.cameras = [];
  }
}

export class CameraHandler {
  public viewer: Viewer;
  public cameraScene: Scene;
  public markerScene: Scene;
  public sceneViewController: SceneViewCameras;
  public aerialController: AerialCameras;
  public cameras: CameraImage[] = [];
  public files: CameraFile[] = [];
  public matrix = new Matrix4();
  public adjustments = {};
  public cameraHeight = 0;
  public aligned = false;
  public waitForCameras = false;
  public current: CameraImage | null = null;
  public newCameraLoading = false
  public cameraLoadedOnce = false;

  constructor(viewer: Viewer, maxTextureSize: number) {
    this.viewer = viewer;
    this.initScenes();

    this.sceneViewController = new SceneViewCameras(this);
    this.aerialController = new AerialCameras(this);

    thumbnailCache.setMaxTextureSize(maxTextureSize);
    standardCache.setMaxTextureSize(maxTextureSize);
  }

  get count() {
    return this.cameras.length;
  }

  get camera() {
    return this.viewer.camera;
  }

  get controls() {
    return this.viewer.controls;
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  get minimap() {
    return this.viewer.minimap;
  }

  get currentIndex() {
    return this.cameras.indexOf(this.current);
  }

  get cameraLoaded() {
    return this.current !== null;
  }

  get renderInFront() {
    return this.sceneViewController.renderInFront;
  }

  get isEmpty() {
    return this.cameras.length === 0;
  }

  get markersVisible() {
    return this.sceneViewController.markersVisible;
  }

  addNewFile(asset: Asset) {
    if (this.getByID(asset.id)) return;

    let file: CameraFile;

    if (asset.type === AssetType.Panoramic) {
      file = new PanoramicCameraFile(this, asset);
    } else if (asset.type === AssetType.Planar) {
      file = new PlanarCameraFile(this, asset);
    } else {
      return;
    }

    this.files.push(file);
  }

  deleteFile(file: CameraFile) {
    file.destroy();

    const index = this.files.indexOf(file);
    this.files.splice(index, 1);
  }

  updateVisibility(asset: Asset) {
    let file = this.getByID(asset.id);
    if (!file) return;

    file.updateVisibility(asset.visible);
  }

  reconcile(camerasFiles: Asset[]) {
    const currentIDs = new Set(this.files.map(x => x.id));
    const newAssetIDs = new Set(camerasFiles.map(x => x.id));
    const assetsToLoad = camerasFiles.filter(x => !currentIDs.has(x.id));
    const filesToDelete = this.files.filter(x => !newAssetIDs.has(x.id));

    let cameraFilesAdded = assetsToLoad.length;
    let cameraFilesRemoved = filesToDelete.length;

    // Delete old camera files
    filesToDelete.forEach(file => {
      this.deleteFile(file);
    });

    // Add new camera files
    assetsToLoad.forEach(asset => {
      this.addNewFile(asset);
    });

    // Toggle visibility for each file
    camerasFiles.forEach(file => {
      this.updateVisibility(file);
    });

    // Update combined camera array
    this.cameras = this.files
      .filter(file => file.visible)
      .map(file => file.cameras)
      .flat();

    // Current camera has been removed / hidden
    if (this.current && !this.cameras.includes(this.current)) {
      this.viewer.clearCurrentCamera();
    }

    // All camera files have been removed
    if (this.files.length === 0) {
      this.cameraLoadedOnce = false;
    }

    return {cameraFilesAdded, cameraFilesRemoved};
  }

  initScenes() {
    this.cameraScene = new Scene();
    this.cameraScene.add(new AmbientLight(0xFFFFFF));

    this.markerScene = new Scene();
    this.markerScene.add(new AmbientLight(0xFFFFFF));
  }

  getFeatureForEvent(event) {
    return this.aerialController.getFeatureForEvent(event);
  }

  onMouseMove(event: CustomMouseEvent) {
    this.sceneViewController.onMouseMove(event);
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    return this.sceneViewController.onMouseUp(event);
  }

  onKeyDown(event) {
    if (event.key === "ArrowUp") {
      this.incrementCamera(1);
    } else if (event.key === "ArrowDown") {
      this.incrementCamera(-1);
    }
  }

  setWaitForCameras(state) {
    this.waitForCameras = state;
  }

  /** @private */
  setNormalCamera() {
    this.controls.setOrbitValues(false);
  }

  /** @private */
  setOrbitCamera() {
    this.controls.setOrbitValues(true);
  }

  setNavigationType(type: MarkerNavType) {
    this.sceneViewController.setNavigationType(type);
  }

  setCameraHeight(height: number) {
    this.cameraHeight = height;
  }

  /** @private */
  setCurrentImage(cameraImage: CameraImage | null) {
    this.current = cameraImage;
  }

  getByID(assetID: string) {
    return this.files.find(file => file.id === assetID);
  }

  getCameraByName(cameraName: string): CameraImage {
    for (let file of this.files) {
      const camera = file.getByName(cameraName);
      if (camera) return camera;
    }
  }

  getCameraByID(cameraID: string): CameraImage {
    for (let file of this.files) {
      const camera = file.getByID(cameraID);
      if (camera) return camera;
    }
  }

  getCamerasByCSV(assetID: string): CameraImage[] {
    const file = this.getByID(assetID);
    return file ? file.cameras : [];
  }

  getCamerasByFolder(folderID: string): CameraImage[] {
    return this.files
      .filter(file => file.folderID === folderID)
      .map(file => file.cameras)
      .flat();
  }

  getRowsCSV(cameras: CameraImage[]) {
    let csvEncompassOut = [];
    csvEncompassOut.push(['Filename', 'X', 'Y', 'Z','Roll', 'Pitch', 'Yaw']);

    cameras.forEach(camera => {
      let position = camera.position.value;
      let rotation = camera.rotation.degrees;

      csvEncompassOut.push([
        camera.name,
        position.x, position.y, position.z,
        rotation.x, rotation.y, rotation.z
      ]);
    });

    return csvEncompassOut;
  }

  async loadImage(cameraID, opts:CameraLoadOptions = {}) {
    if (this.newCameraLoading) return;

    let camera = this.getCameraByID(cameraID);
    if (!camera) return;

    const {centerOnImage=true} = opts;

    if (camera.enabled) {
      // Reload existing camera
      console.log(`Reload camera: ${camera.name}`);
      this.setNormalCamera();
      camera.setCameraParameters(opts);
      this.minimap.setCurrentCamera(camera, centerOnImage);
      return camera;
    }

    // Load new camera
    console.log(`Load camera: ${camera.name}`);

    this.newCameraLoading = true;

    if (!this.cameraLoaded) {
      this.setWaitForCameras(true);
    }

    // Load image data from path
    camera.initialize();
    await camera.load();

    await this.viewer.updatePreviousImage();

    // Destroy previous cameras and exit orbit mode
    this.clearImages(camera);
    this.setNormalCamera();
    this.minimap.setCurrentCamera(camera, centerOnImage);

    camera.activate(opts);

    this.setCurrentImage(camera);
    this.setWaitForCameras(false);
    this.updateArrowNavigation();

    this.cameraLoadedOnce = true;
    this.newCameraLoading = false;

    return camera;
  }

  updateArrowNavigation() {
    this.sceneViewController.updateArrowNavigation();
  }

  clearArrowNavigation() {
    this.sceneViewController.clearArrowNavigation();
  }

  clearImages(currentCamera: CameraImage) {
    /** Destroy all images except the current one */
    this.cameras.forEach(camera => {
      if (camera.id === currentCamera.id) {
        return;
      }
      camera.destroy();
    });
  }

  setMeshVisible(state) {
    this.cameras.forEach(camera => {
      if (state && !camera.enabled) {
        return;
      }

      if (camera.object) {
        camera.object.visible = state;
      }
    });
  }

  incrementCamera(increment) {
    if (!this.current) return;

    let cameraIndex = this.currentIndex;
    let nextIndex = cameraIndex + increment;
    nextIndex = (nextIndex + this.count) % this.count ;
    let nextCamera = this.cameras[nextIndex];
    this.viewer.loadImage(nextCamera.id);
  }

  setCameraAdjustments(newAdjustments) {
    if (isEqual(this.adjustments, newAdjustments)) return;

    this.adjustments = newAdjustments;
    this.updateAdjustments();
  }

  setCameraMatrix(elements) {
    const newMatrix = new Matrix4().fromArray(elements);
    if (this.matrix.equals(newMatrix)) return;

    this.matrix = newMatrix;
    this.updateMatrix();
  }

  /** Reset to identity matrix */
  resetCameraMatrix() {
    this.matrix = new Matrix4();
    this.updateMatrix();
  }

  resetTextureCache() {
    thumbnailCache.reset();
    standardCache.reset();
  }

  /** @private */
  updateMatrix() {
    this.files.forEach(file => {
      file.updateMatrix(this.matrix);
    });

    if (this.current) {
      this.setNormalCamera();
      this.current.setCameraPosition();
    }

    this.updateArrowNavigation();
  }

  /** @private */
  updateAdjustments() {
    this.files.forEach(file => {
      file.updateAdjustments(this.adjustments);
    });
  }

  /** @private */
  loadSavedCameraState(savedCameraState: SceneCameraState) {
    let savedcameraID;
    const activeCamera = savedCameraState.camera;
    const savedLookat = savedCameraState.lookat;

    if (activeCamera) {
      // Load saved camera based on saved camera id
      const camera = this.getCameraByID(activeCamera);

      if (camera) {
        savedcameraID = activeCamera;
      }
    } else if (savedLookat) {
      // Load closest camera based on saved position
      const lookat = new DataProjectionCoordinate(savedLookat);
      const closestCameraID = this.closestCameraToPosition(lookat);

      if (closestCameraID) {
        savedcameraID = closestCameraID;
      } else {
        toast.warning(t("toast.camera-error-xyz"));
      }
    }

    if (!savedcameraID) return false;

    // Convert from SceneCameraState to CameraLoadOptions
    const lookatVector = savedCameraState.lookat
      ? new DataProjectionCoordinate(savedCameraState.lookat)
      : null;

    const cameraLoadOptions = {
      fov: savedCameraState.fov,
      angles: savedCameraState.angles,
      lookat: lookatVector,
      centerOnImage: false
    } as CameraLoadOptions;

    this.viewer.loadImage(savedcameraID, cameraLoadOptions);

    return true;
  }

  closestCameraToPosition(position: DataProjectionCoordinate): number | undefined {
    const maxViewAngle = MathUtils.degToRad(45);
    const maxDistance = 100;

    let bestDistance = Infinity;
    let bestCameraID;

    for (const camera of this.cameras) {
      const cameraPos = camera.position.value;
      const distance = position.distanceTo(cameraPos);
      if (distance > bestDistance) continue;
      if (distance > maxDistance) continue;

      const angles = positionToCameraAngle(cameraPos, position);
      const phiAngle = Math.abs(angles[1]);
      if (phiAngle > maxViewAngle) continue;

      bestDistance = distance;
      bestCameraID = camera.id;
    }

    return bestCameraID;
  }

  update() {
    this.sceneViewController.update();
  }
}

const multiplyRotations = (rotationA, rotationB) => {
  let matrixA = rotationA instanceof Euler
    ? new Matrix4().makeRotationFromEuler(rotationA)
    : rotationA;

  let matrixB = rotationB instanceof Euler
    ? new Matrix4().makeRotationFromEuler(rotationB)
    : rotationB;

  return new Matrix4().multiplyMatrices(matrixA, matrixB);
};

export const directionToCameraAngle = (direction: Vector3) : [number, number] => {
  return positionToCameraAngle(new Vector3(), direction);
};

export const positionToCameraAngle = (cameraPos: Vector3, position: Vector3) : [number, number] => {
  const sphere = new SphericalPosition().fromVectors(cameraPos, position);
  const theta = MathUtils.degToRad(sphere.theta);
  const phi = MathUtils.degToRad(90 - sphere.phi);

  return [theta, phi] as [number, number];
};